import { Fragment } from "react";
import { Link } from "react-router-dom";

import type { BulkCommitResponse } from "../api/types";
import { RESULT_LABELS } from "./labels";
import { IssueList } from "./ImportPreview";

/**
 * What actually happened, product by product.
 *
 * Products commit one at a time, so this is genuinely a list of outcomes rather
 * than one verdict: a product that hit a runtime conflict does not discard the
 * ones beside it, and saying so is the only honest way to report a batch.
 *
 * IT REPORTS THE PRODUCT OPERATION AND NOTHING ELSE. This screen imported
 * catalogue data; it did not photograph anything, and it does not claim to
 * have. What a newly created draft still needs is its photographs, so each one
 * links to the Edit screen where they are attached in the family's own
 * vocabulary, against the variant they actually belong to.
 *
 * A product marked "already applied" is a retry finishing what an interrupted
 * attempt started. It is shown rather than hidden, because an operator who
 * pressed the button twice is entitled to know that the second press changed
 * nothing.
 */
export function ImportResult({
  result,
  onStartOver,
}: {
  result: BulkCommitResponse;
  onStartOver: () => void;
}) {
  const { summary } = result;
  return (
    <div className="stack" data-testid="import-result">
      <div className="bulk__summary" role="status">
        <Figure label="Created" value={summary.created} />
        <Figure label="Updated" value={summary.updated} />
        <Figure label="No change" value={summary.unchanged} />
        <Figure label="Failed" value={summary.failed} tone="bad" />
      </div>

      <p className="muted">
        Everything that was imported is a <strong>draft</strong>, and no
        photographs were imported with it. Open each new product to add its
        images, then{" "}
        <Link to="/products">review and publish it in Product Management</Link>,
        which validates a product before customers see it. No stock quantity was
        changed.
      </p>

      <div className="bulk__scroll">
        <table className="table">
          <caption className="sr-only">What this import did</caption>
          <thead>
            <tr>
              <th scope="col">Row group</th>
              <th scope="col">Product</th>
              <th scope="col">Product result</th>
              <th scope="col" className="num">
                Variants added
              </th>
              <th scope="col">Next</th>
            </tr>
          </thead>
          <tbody>
            {result.products.map((product) => (
              <Fragment key={product.product_ref}>
                <tr>
                  <td className="muted">{product.product_ref}</td>
                  <td>
                    <span className="bulk__product">
                      {product.name ?? "—"}
                    </span>
                    <span className="bulk__code">
                      {product.product_code ?? "No code"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={
                        product.result === "failed"
                          ? "badge badge--out_of_stock"
                          : "badge badge--in_stock"
                      }
                    >
                      {RESULT_LABELS[product.result]}
                    </span>
                    {product.replayed ? (
                      <span className="bulk__note">Already applied</span>
                    ) : null}
                  </td>
                  <td className="num">{product.created_variants}</td>
                  <td>
                    {/* The one thing a new draft still needs, and where it is
                        done. Ordinary navigation to the ordinary screen — the
                        images themselves are chosen there, per variant. */}
                    {product.product_id && product.result !== "failed" ? (
                      <Link
                        className="btn btn--sm btn--subtle"
                        to={`/products/${product.product_id}/edit`}
                      >
                        {product.result === "created"
                          ? "Add photographs"
                          : "Edit product"}
                      </Link>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
                {product.issues.length > 0 ? (
                  <tr className="bulk__issueRow">
                    <td colSpan={5}>
                      <IssueList
                        issues={product.issues}
                        caption={`Why ${product.product_ref} failed`}
                      />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row">
        <button type="button" className="btn" onClick={onStartOver}>
          Import another workbook
        </button>
      </div>
    </div>
  );
}

function Figure({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "bad";
}) {
  const className =
    tone && value > 0 ? `bulk__figure bulk__figure--${tone}` : "bulk__figure";
  return (
    <div className={className}>
      <span className="bulk__figureValue num">{value}</span>
      <span className="bulk__figureLabel">{label}</span>
    </div>
  );
}
